<template>
  <v-menu
    v-model="isMenuVisible"
    :close-on-content-click="false"
    max-width="290px"
    min-width="290px"
    offset-y
    :disabled="disabled || $attrs.readonly"
  >
    <template #activator="{ on }">
      <data-input-text
        v-bind="{ ...$props, ...$attrs }"
        v-model="formattedDate"
        v-on="{ ...on }"
        @input="setValue"
        :rules="computedRules"
        class="included"
      />
      <v-container v-if="defaultRanges.length > 0">
        <v-row class="justify-start pt-2">
          <v-col v-for="(defaultRange, idx) in defaultRanges" :key="idx" cols="auto" class="px-0 pr-1 py-0 pb-1">
            <v-chip
              :color="
                date[0] !== defaultRange.value[0] || date[1] !== defaultRange.value[1] ? 'rgba(0,0,0,0.12)' : '#E8F0FE'
              "
              label
              small
              @click="date = defaultRange.value"
              :disabled="disabled"
            >
              <v-col
                cols="auto"
                class="px-0 date-range-chip"
                :style="
                  `color: ${
                    date[0] !== defaultRange.value[0] || date[1] !== defaultRange.value[1]
                      ? 'rgba(0,0,0,0.87)'
                      : '#1A73E8'
                  }`
                "
              >
                {{ defaultRange.text }}
              </v-col>
            </v-chip>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <input-card
      @save="save"
      @cancel="cancel"
      :is-save-disabled="isSaveDisabled || isRangeInvalid"
      v-click-outside="{
        handler: onClickOutside,
        include,
        closeConditional,
      }"
    >
      <v-date-picker v-model="date" range :type="returnType" v-bind="datePickerProps" full-width />
    </input-card>
  </v-menu>
</template>
<script>
import inputCard from '@/mixins/inputCard';

import DataInputText from '@/components/DataInputText';
import InputCard from '@/components/InputCard';

export default {
  name: 'DataInputDaterange',
  mixins: [inputCard],
  components: {
    DataInputText,
    InputCard,
  },
  props: {
    value: {
      type: [Array],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    localeDateFormat: {
      default: 'MMMM YYYY',
    },
    datePickerProps: {
      type: Object,
    },
    defaultRanges: {
      type: Array,
      default: () => {
        return [];
      },
    },
    returnType: {
      type: String,
      default: 'month',
      validator: value => {
        return ['month', 'date'].includes(value);
      },
    },
  },
  data: () => ({
    isMenuVisible: false,
    formattedDate: undefined,
  }),
  computed: {
    computedFormattedDate() {
      if (!this.value || this.value.length === 0) {
        return;
      }
      return this.value.map(v => this.$dayjs(v).format(this.localeDateFormat)).join(' - ');
    },
    computedRules() {
      return [...(this.$attrs.rules || []), this.isValidRule];
    },
    isRangeInvalid() {
      if (this.value.length < 2) {
        return true;
      }
      const date1 = this.$dayjs(this.value[0]);
      const date2 = this.$dayjs(this.value[1]);
      if (this.datePickerProps && this.datePickerProps.min) {
        const dateMin = this.$dayjs(this.datePickerProps.min);
        if (date1.isBefore(dateMin) || date2.isBefore(dateMin)) {
          return true;
        }
      }
      if (this.datePickerProps && this.datePickerProps.max) {
        const dateMax = this.$dayjs(this.datePickerProps.max);
        if (dateMax.isBefore(date1) || dateMax.isBefore(date2)) {
          return true;
        }
      }
      return false;
    },
    isValid() {
      if (!this.formattedDate) {
        return true;
      }
      for (const date of this.formattedDate.split(' - ')) {
        const dayjsDate = this.$dayjs(date, this.localeDateFormat, process.env.VUE_APP_I18N_LOCALE, true);
        if (!dayjsDate.isValid()) {
          return false;
        }
      }
      if (this.isRangeInvalid) {
        return this.$i18n.t('sidebar.invalidDateRange');
      }
      return true;
    },
    isValidRule() {
      return (
        this.isValid ||
        this.$i18n.t('rules.requiredFormat', {
          format: `${this.localeDateFormat} - ${this.localeDateFormat}`,
        })
      );
    },
    date: {
      get() {
        if (!this.value) {
          const now = this.$dayjs()
            .toISOString()
            .split('T')[0];
          return [now, now];
        }
        return this.value.map(v => v.split('T')[0]);
      },
      set(nV) {
        if (nV.length > 1) {
          const date1 = this.$dayjs(nV[0]);
          const date2 = this.$dayjs(nV[1]);
          if (date2.isBefore(date1)) {
            nV = [nV[1], nV[0]];
            this.$emit('input', nV);
            return;
          }
        }
        this.$emit('input', nV);
      },
    },
  },
  watch: {
    computedFormattedDate: {
      immediate: true,
      handler(nV) {
        this.formattedDate = nV;
      },
    },
  },
  methods: {
    setValue() {
      if (!this.isValid) {
        return;
      }
      this.$emit('input', this.getValue());
    },
    getValue(formattedDate = this.formattedDate) {
      let formattedDateSplit = formattedDate ? formattedDate.split(' - ') : '';
      if (!formattedDate || formattedDateSplit.length < 1) {
        return;
      }

      if (formattedDateSplit.length > 1) {
        const date1 = this.$dayjs(formattedDateSplit[0], this.localeDateFormat, process.env.VUE_APP_I18N_LOCALE, true);
        const date2 = this.$dayjs(formattedDateSplit[1], this.localeDateFormat, process.env.VUE_APP_I18N_LOCALE, true);
        if (date2.isBefore(date1)) {
          formattedDateSplit = [formattedDateSplit[1], formattedDateSplit[0]];
        }
      }

      return formattedDateSplit.map(date =>
        this.$dayjs
          .utc(date, this.localeDateFormat, process.env.VUE_APP_I18N_LOCALE, true)
          .toISOString()
          .substring(0, this.returnType === 'month' ? 7 : 10)
      );
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .v-chip--label {
    border-radius: 12px !important;
  }
}
.date-range-chip {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
}
</style>
